/**
 * Generic n‑period‑over‑lagged‑n‑period momentum, in %.
 * (((sum of last window periods) / (sum of the window periods ending lag ago) / divisor) - 1) × 100
 * @param values   array of numbers, sorted oldest→newest
 * @param window   how many periods to sum (e.g. 4)
 * @param lag      how many periods back the prior window starts
 * @param div      divisor to scale the result (e.g. 1)
 */
const calculateMomentum = (values: number[], window: number, lag: number, div: number) => {
  const n = values.length;
  if (n < window + lag) return 0;

  // sum of most‐recent `window` periods
  const currentSum = values.slice(n - window, n).reduce((acc, v) => acc + v, 0);

  // sum of the `window` periods ending `lag` ago
  const priorSum = values.slice(n - window - lag, n - window).reduce((acc, v) => acc + v, 0);

  if (priorSum === 0) return 0;
  const result = ((currentSum / (priorSum / div)) - 1) * 100;
  // console.log(`Momentum: currentSum=${currentSum} / (priorSum=${priorSum} / divider=${div}) = result=${Math.round(result)}`);
  return result;
};

/**
 * Computes the short-term momentum for a competitor or brick.
 * Typically, this measures the change in value (e.g., sales, patients)
 * over a recent, short period (such as the last 4 months).
 * Used to identify recent trends or rapid changes in performance.
 * @param {number[]} values Array of sales data objects, sorted by date (oldest to newest)
 * @returns The difference between the latest value and the value N periods ago
 */
export const computeMomentumShort = (values: number[]) => {
  return calculateMomentum(values, 4, 4, 1); // ([1-4] / ([5-8] / 1)) * 100
};

/**
 * Computes the long-term momentum for a competitor or brick.
 * This measures the change in value (e.g., sales, patients)
 * over a longer period (such as the last year).
 * Used to assess sustained growth or decline over time.
 * @param  {number[]} values
 * @returns The difference between the latest value and the value M periods ago
 */
export const computeMomentumLong = (values: number[]) => {
  return calculateMomentum(values, 4, 12, 3); // ([1-4] / ([5-16] / 3)) * 100
};
